import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { toReduxError } from '@universal/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { BenefitsResponse, BenefitsType } from '@appTypes/benefitsType';
import { Actions, actions } from '@redux/modules/benefits';
import { TypedEpic } from './types';

const fetchBenefitsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_BENEFITS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getBenefits.request>[]
    >,
    switchMap(([action]: { payload: string }[]) => {
      // Parse the query string from the payload
      const searchParams = new URLSearchParams(action.payload);
      const params = Object.fromEntries(searchParams.entries());

      return fromApiRequest((signal) =>
        apiClient.get<BenefitsResponse>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].benefits.list,
          params,
        }),
      ).pipe(
        map((response) => actions.getBenefits.success(response.data)),
        catchError((err) => of(actions.getBenefits.failure(toReduxError(err, 'Could not get benefits')))),
      );
    }),
  );

const fetchMoreBenefitsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MORE_BENEFITS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMoreBenefits.request>[]
    >,
    switchMap(([action]: { payload: string }[]) => {
      // Parse the query string from the payload
      const searchParams = new URLSearchParams(action.payload);
      const params = Object.fromEntries(searchParams.entries());

      return fromApiRequest((signal) =>
        apiClient.get<BenefitsResponse>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].benefits.list,
          params,
        }),
      ).pipe(
        map((response) => actions.getMoreBenefits.success(response.data)),
        catchError((err) => of(actions.getMoreBenefits.failure(toReduxError(err, 'Could not get benefits')))),
      );
    }),
  );

const fetchBenefitArticleEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_BENEFIT_ARTICLE),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getBenefitArticle.request>[]
    >,
    switchMap(([action]: { payload: string }[]) =>
      fromApiRequest((signal) =>
        apiClient.get<BenefitsType>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].documents.get(action.payload),
        }),
      ).pipe(
        map((response) => actions.getBenefitArticle.success(response.data)),
        catchError((err) => of(actions.getBenefitArticle.failure(toReduxError(err, 'Could not get benefit article')))),
      ),
    ),
  );

export const epics: TypedEpic[] = [fetchBenefitsEpic, fetchMoreBenefitsEpic, fetchBenefitArticleEpic];
